import React, { useEffect, useState } from "react";
import {
  useGetOrganisationSettingsQuery,
  useSetOrganisationPowerDialerCallbackMessageMutation,
  useSetOrganisationPowerDialerOptOutDigitMutation,
  useSetOrganisationQuietHoursPolicyMutation,
  useSetOrganisationTranscriptionEnabledMutation,
  useSetOrganisationTranscriptionProviderMutation,
} from "@/store/admin_api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { apiErrorMessage } from "@/util/apiError";
import { toast } from "sonner";

const PLATFORM_DEFAULT = "PLATFORM_DEFAULT";
const OPT_OUT_DIGITS = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];

interface OrganisationSettingsTabProps {
  organisationId: string;
}

export default function OrganisationSettingsTab({ organisationId }: OrganisationSettingsTabProps) {
  const { data: settings, isLoading } = useGetOrganisationSettingsQuery(organisationId);
  const [setTranscriptionProvider, { isLoading: isSaving }] = useSetOrganisationTranscriptionProviderMutation();
  const [setTranscriptionEnabled, { isLoading: isTogglingEnabled }] = useSetOrganisationTranscriptionEnabledMutation();
  const [setQuietHoursPolicy, { isLoading: isSavingQuietHours }] = useSetOrganisationQuietHoursPolicyMutation();
  const [setCallbackMessage, { isLoading: isSavingCallbackMessage }] =
    useSetOrganisationPowerDialerCallbackMessageMutation();
  const [setOptOutDigit, { isLoading: isSavingOptOutDigit }] = useSetOrganisationPowerDialerOptOutDigitMutation();
  const [callbackMessage, setCallbackMessageDraft] = useState("");

  function quietHoursOptionFor(policyName: string) {
    return settings?.availableQuietHoursPolicies.find((option) => option.name === policyName);
  }

  async function changeQuietHoursPolicy(value: string) {
    try {
      await setQuietHoursPolicy({
        organisationId,
        policy: value === PLATFORM_DEFAULT ? null : value,
      }).unwrap();
      toast.success("Quiet hours policy updated");
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, "Failed to update the quiet hours policy"));
    }
  }

  function displayNameFor(providerName: string) {
    return (
      settings?.availableTranscriptionProviders.find((option) => option.name === providerName)?.displayName ??
      providerName
    );
  }

  async function changeEnabled(enabled: boolean) {
    try {
      await setTranscriptionEnabled({ organisationId, enabled }).unwrap();
      toast.success(enabled ? "Transcription enabled" : "Transcription disabled");
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, "Failed to update transcription"));
    }
  }

  async function changeProvider(value: string) {
    try {
      await setTranscriptionProvider({
        organisationId,
        provider: value === PLATFORM_DEFAULT ? null : value,
      }).unwrap();
      toast.success("Transcription provider updated");
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, "Failed to update the transcription provider"));
    }
  }

  async function saveCallbackMessage(message: string) {
    try {
      await setCallbackMessage({ organisationId, message: message.trim() }).unwrap();
      toast.success(message.trim() ? "Callback message saved" : "Callback message reset to the built-in default");
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, "Failed to save the callback message"));
    }
  }

  async function changeOptOutDigit(value: string) {
    try {
      await setOptOutDigit({ organisationId, optOutDigit: value }).unwrap();
      toast.success("Opt-out key updated");
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, "Failed to update the opt-out key"));
    }
  }

  useEffect(() => {
    setCallbackMessageDraft(settings?.effectivePowerDialerCallbackMessage ?? "");
  }, [settings?.effectivePowerDialerCallbackMessage]);

  if (isLoading || !settings) {
    return <Skeleton className="h-64 w-full" />;
  }

  const effectiveQuietHours = quietHoursOptionFor(settings.effectiveQuietHoursPolicy);
  const platformQuietHours = quietHoursOptionFor(settings.platformQuietHoursPolicy);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Transcription</CardTitle>
          <CardDescription>
            The provider is yours to set. The organisation controls only whether transcription is on, and sees its rate.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between gap-6 rounded-lg border p-4">
            <div>
              <p className="font-medium">Transcription enabled</p>
              <p className="text-sm text-muted-foreground">
                The organisation controls this from their telephony settings. Turning it off here stops every voicemail
                step from transcribing, so nothing can be charged.
              </p>
            </div>
            <Switch
              checked={settings.transcriptionEnabled}
              onCheckedChange={changeEnabled}
              disabled={isTogglingEnabled}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="transcription-provider">Provider</Label>
            <Select
              value={settings.transcriptionProviderOverride ?? PLATFORM_DEFAULT}
              onValueChange={changeProvider}
              disabled={isSaving}
            >
              <SelectTrigger id="transcription-provider" className="w-72">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={PLATFORM_DEFAULT}>
                  Platform default ({displayNameFor(settings.platformTranscriptionProvider)})
                </SelectItem>
                {settings.availableTranscriptionProviders.map((option) => (
                  <SelectItem key={option.name} value={option.name}>
                    {option.displayName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              Overriding moves this organisation onto a different provider, for example when they are unhappy with
              transcription quality. Their rate follows the provider.
            </p>
          </div>

          <div className="rounded-lg border p-4">
            <div className="flex items-center justify-between border-b py-2">
              <span className="text-sm text-muted-foreground">In use</span>
              <span className="text-sm font-medium">
                {displayNameFor(settings.effectiveTranscriptionProvider)}
                {settings.transcriptionProviderOverride && (
                  <Badge variant="outline" className="ml-2">
                    Override
                  </Badge>
                )}
              </span>
            </div>
            <div className="flex items-center justify-between border-b py-2">
              <span className="text-sm text-muted-foreground">Our cost per minute</span>
              <span className="text-sm font-medium tabular-nums">${settings.transcriptionBaseRate}</span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-muted-foreground">They pay per minute</span>
              <span className="text-sm font-medium tabular-nums">${settings.transcriptionRate}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Quiet hours</CardTitle>
          <CardDescription>
            How this organisation's calls and texts behave outside 8 AM – 9 PM in the recipient's own timezone.
            Overrides the platform default in both directions. Automated paths (AI agents, workflows) never prompt —
            they are blocked outright unless this is set to Off.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {settings.effectiveQuietHoursPolicy === "OFF" && (
            <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-4">
              <p className="text-sm font-medium">This organisation has no quiet hours</p>
              <p className="text-sm text-muted-foreground">
                {settings.quietHoursPolicyOverride
                  ? "It is set to Off here, so it can call and text at any hour with no confirmation and no audit record."
                  : "It inherits Off from the platform default. Set a policy here to override that for this organisation alone."}
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="quiet-hours-policy">Policy</Label>
            <Select
              value={settings.quietHoursPolicyOverride ?? PLATFORM_DEFAULT}
              onValueChange={changeQuietHoursPolicy}
              disabled={isSavingQuietHours}
            >
              <SelectTrigger id="quiet-hours-policy" className="w-72">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={PLATFORM_DEFAULT}>
                  Platform default ({platformQuietHours?.displayName ?? settings.platformQuietHoursPolicy})
                </SelectItem>
                {settings.availableQuietHoursPolicies.map((option) => (
                  <SelectItem key={option.name} value={option.name}>
                    {option.displayName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">{effectiveQuietHours?.description}</p>
          </div>

          <div className="rounded-lg border p-4">
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-muted-foreground">In use</span>
              <span className="text-sm font-medium">
                {effectiveQuietHours?.displayName ?? settings.effectiveQuietHoursPolicy}
                {settings.quietHoursPolicyOverride && (
                  <Badge variant="outline" className="ml-2">
                    Override
                  </Badge>
                )}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Power dialer callback message</CardTitle>
          <CardDescription>
            Played to a lead who answers while the agent is busy, only on numbers with written call consent. Campaigns
            can replace it with their own text. It has to say the call was for telemarketing purposes, name the
            business with {"{business}"}, give the callback number with {"{number}"} and explain the opt-out key with{" "}
            {"{digit}"}.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="callback-message">Message</Label>
            <Textarea
              id="callback-message"
              rows={4}
              value={callbackMessage}
              onChange={(event) => setCallbackMessageDraft(event.target.value)}
            />
            <div className="flex items-center gap-2">
              <Button onClick={() => void saveCallbackMessage(callbackMessage)} disabled={isSavingCallbackMessage}>
                {isSavingCallbackMessage ? "Saving..." : "Save message"}
              </Button>
              {settings.powerDialerCallbackMessageOverride && (
                <Button variant="outline" onClick={() => void saveCallbackMessage("")} disabled={isSavingCallbackMessage}>
                  Reset to default
                </Button>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="opt-out-digit">Opt-out key</Label>
            <Select value={settings.powerDialerOptOutDigit} onValueChange={changeOptOutDigit} disabled={isSavingOptOutDigit}>
              <SelectTrigger id="opt-out-digit" className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {OPT_OUT_DIGITS.map((digit) => (
                  <SelectItem key={digit} value={digit}>
                    {digit}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              Pressing this key during the message adds the number to the organisation's Do Not Call list and ends the
              call.
            </p>
          </div>

          <div className="rounded-lg border p-4">
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-muted-foreground">In use</span>
              <span className="text-sm font-medium">
                {settings.powerDialerCallbackMessageOverride ? "Organisation message" : "Built-in message"}
                {settings.powerDialerCallbackMessageOverride && (
                  <Badge variant="outline" className="ml-2">
                    Override
                  </Badge>
                )}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
